import Badge from '@mui/material/Badge';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useEffect, useRef, useState, type FC, type ReactNode } from 'react';
import {
  MdBookmark,
  MdBookmarkBorder,
  MdFilterAltOff,
  MdOutlineFilterAlt,
  MdPerson,
  MdPersonOutline,
} from 'react-icons/md';

import { DateRangeFilter } from './date-range-filter';
import { FeedFilterDrawer, countActiveDrawerFilters } from './feed-filter-drawer';
import { FeedSearchInput } from './feed-search-input';
import { hasActiveFeedFilters, type TFeedSearchParams } from './feed-search-params';
import { FeedSortSelect } from './feed-sort-select';
import { FILTER_ROSE, FILTER_ROSE_BG, FILTER_ROSE_DARK } from './filter-trigger-button';
import { PostTypeFilter } from './post-type-filter';
import { PostedByFilter } from './posted-by-filter';
import { TagFilter } from './tag-filter';

type Props = {
  /** Feed sub-space id — the tag filter loads the container's available tags */
  spaceId: string;
  filters: TFeedSearchParams;
  /** Patch the URL search params — undefined values remove the param */
  onChange: (patch: Partial<TFeedSearchParams>) => void;
};

type QuickFilterChipProps = {
  label: string;
  /** Outline glyph at rest, filled while the filter is on. */
  icon: ReactNode;
  activeIcon: ReactNode;
  active: boolean;
  onToggle: () => void;
};

/**
 * Saved posts / My posts toggle for the mobile bar — the sidebar hosts these
 * quick filters on desktop, but phones have no sidebar. Styled to match the
 * desktop filter trigger buttons (icon and label together, rose fill when on),
 * but stretched to split the full toolbar width on mobile.
 */
const QuickFilterChip: FC<QuickFilterChipProps> = ({ label, icon, activeIcon, active, onToggle }) => (
  <Button
    size="small"
    variant="text"
    onClick={onToggle}
    aria-pressed={active}
    startIcon={active ? activeIcon : icon}
    sx={{
      flex: 1,
      // Full-width buttons, but keep the icon + label as one centered unit
      // instead of letting them spread to the button edges.
      justifyContent: 'center',
      textTransform: 'none',
      borderRadius: '10px',
      height: 34,
      minHeight: 34,
      fontFamily: 'Figtree, sans-serif',
      fontSize: 13,
      fontWeight: 600,
      px: '12px',
      py: 0,
      whiteSpace: 'nowrap',
      border: 'none',
      boxShadow: 'none',
      // Match FilterTriggerButton: rose fill + dark-rose text when on,
      // transparent with neutral grey text/icon at rest.
      bgcolor: active ? FILTER_ROSE_BG : 'transparent',
      transition: 'background-color 0.15s ease, color 0.15s ease',
      '&.MuiButton-textPrimary': { color: active ? FILTER_ROSE_DARK : '#666' },
      // Keep the icon tight against the label so they read as one unit.
      '& .MuiButton-startIcon': { ml: 0, mr: '6px', color: active ? FILTER_ROSE_DARK : '#666' },
      '&:hover': { border: 'none' },
      '&&.MuiButton-textPrimary:hover': { boxShadow: 'none', bgcolor: '#F4F4F4' },
    }}
  >
    {label}
  </Button>
);

/**
 * Feed Filter Bar — one toolbar card above the feed.
 *
 * Row 1: full-text search + sort. Row 2 (divider-separated): the filter
 * dropdowns (post type, date range, posted by, tags) with a clear-all action
 * pinned right. Filters combine with AND logic.
 *
 * On mobile the four dropdown filters collapse into a single "Filters" button
 * that opens a bottom sheet; search and sort stay inline.
 */
export const FeedFilterBar: FC<Props> = ({ spaceId, filters, onChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [drawerOpen, setDrawerOpen] = useState(false);

  // "Stuck" detection: a zero-height sentinel right above the sticky wrapper.
  // Once it leaves the visible area (scrolled past the 64px app header on
  // xs/sm, or clipped by the scrolling feed column on md+), the bar is stuck
  // and everything except the search input collapses away.
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) {
      return;
    }
    const observer = new IntersectionObserver(([entry]) => setStuck(!entry.isIntersecting), {
      rootMargin: '-64px 0px 0px 0px',
    });
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);

  const handleClearAll = () => {
    onChange({
      q: undefined,
      types: undefined,
      from: undefined,
      to: undefined,
      by: undefined,
      byName: undefined,
      tags: undefined,
      saved: undefined,
      mine: undefined,
    });
  };

  const activeDrawerCount = countActiveDrawerFilters(filters);

  return (
    <>
      <Box ref={sentinelRef} aria-hidden sx={{ height: '1px', mt: '-1px' }} />
      {/* Sticky wrapper: matches the page background (#f4efee, set on <body>)
          and sits flush with the sticky app header (64px on xs/sm, window
          scroll) or the feed column's top edge (md+, column scroll), so posts
          scrolling past disappear cleanly behind it. */}
      <Box
        sx={{
          position: 'sticky',
          top: { xs: '64px', md: 0 },
          zIndex: 20,
          bgcolor: '#f4efee',
          pb: '4px',
          mb: 1,
        }}
      >
        <Box
          sx={{
            p: '8px',
            bgcolor: 'common.white',
            border: '1px solid #ececec',
            borderRadius: '14px',
            boxShadow: '0 4px 14px rgba(0,0,0,0.05)',
          }}
        >
          {/* On phones: big search + an always-visible icon-only filter button
              opening the drawer (sort/filters/clear all live inside). From md
              up the sort shares the row and the filter buttons sit below. */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <FeedSearchInput value={filters.q} onChange={q => onChange({ q })} />
            {isMobile ? (
              <Badge
                badgeContent={activeDrawerCount}
                sx={{
                  '& .MuiBadge-badge': {
                    bgcolor: FILTER_ROSE,
                    color: 'common.white',
                    fontFamily: 'Figtree, sans-serif',
                    fontSize: 10,
                    fontWeight: 700,
                  },
                }}
              >
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  aria-label="Filters"
                  sx={{
                    width: 46,
                    height: 46,
                    borderRadius: '12px',
                    border: '1px solid',
                    borderColor: activeDrawerCount > 0 ? FILTER_ROSE : 'grey.300',
                    bgcolor: activeDrawerCount > 0 ? FILTER_ROSE_BG : 'common.white',
                    color: activeDrawerCount > 0 ? FILTER_ROSE_DARK : '#555',
                    flexShrink: 0,
                  }}
                >
                  <MdOutlineFilterAlt size={21} />
                </IconButton>
              </Badge>
            ) : (
              <FeedSortSelect value={filters.sort} onChange={sort => onChange({ sort })} />
            )}
          </Box>
          {/* Quick filters — the sidebar hosts them on desktop, but phones
              have no sidebar; tuck them away while the bar is stuck. */}
          {isMobile && (
            <Collapse in={!stuck}>
              <Box
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  flexWrap: 'wrap',
                  mt: '12px',
                  pt: '12px',
                  borderTop: '1px solid #f2f2f2',
                }}
              >
                <QuickFilterChip
                  label="Saved posts"
                  icon={<MdBookmarkBorder size={15} />}
                  activeIcon={<MdBookmark size={15} />}
                  active={!!filters.saved}
                  onToggle={() => onChange({ saved: filters.saved ? undefined : true })}
                />
                <QuickFilterChip
                  label="My posts"
                  icon={<MdPersonOutline size={15} />}
                  activeIcon={<MdPerson size={15} />}
                  active={!!filters.mine}
                  onToggle={() => onChange({ mine: filters.mine ? undefined : true })}
                />
              </Box>
            </Collapse>
          )}
          {/* Desktop filter row — always visible; only the mobile bar tucks
              its quick filters away on scroll. */}
          {!isMobile && (
            <Box
              sx={{
                display: 'flex',
                gap: 0.75,
                flexWrap: 'wrap',
                alignItems: 'center',
                mt: '8px',
                pt: '8px',
                borderTop: '1px solid #f2f2f2',
              }}
            >
              <PostTypeFilter spaceId={spaceId} value={filters.types} onChange={types => onChange({ types })} />
              <TagFilter spaceId={spaceId} value={filters.tags} onChange={tags => onChange({ tags })} />
              <PostedByFilter
                spaceId={spaceId}
                value={filters.by}
                valueName={filters.byName}
                onChange={user => onChange(user)}
              />
              <DateRangeFilter from={filters.from} to={filters.to} onChange={range => onChange(range)} />
              <Button
                variant="text"
                size="small"
                onClick={handleClearAll}
                disabled={!hasActiveFeedFilters(filters)}
                startIcon={<MdFilterAltOff size={18} />}
                sx={{
                  // Neutral pill (transparent rest, soft-grey hover, #E2E2E2 when
                  // disabled) with a primary label — the theme colors
                  // `.MuiButton-textPrimary` with primary.main, so no color
                  // override is needed.
                  textTransform: 'none',
                  borderRadius: '10px',
                  height: 34,
                  minHeight: 34,
                  fontFamily: 'Figtree, sans-serif',
                  fontSize: 13,
                  fontWeight: 600,
                  px: '12px',
                  py: 0,
                  whiteSpace: 'nowrap',
                  boxShadow: 'none',
                  bgcolor: 'transparent',
                  '&:hover': { boxShadow: 'none', bgcolor: '#f4f4f4' },
                  // Keep the disabled state background-free (text-only) — the
                  // theme otherwise forces a grey[400] fill with !important
                  // (components.ts MuiButton root), so override via `&&` +
                  // !important with a transparent background and greyed label.
                  '&&.Mui-disabled': { background: 'transparent !important', color: '#B8B8B8' },
                }}
              >
                Clear
              </Button>
            </Box>
          )}
        </Box>
        {isMobile && (
          <FeedFilterDrawer
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            spaceId={spaceId}
            filters={filters}
            onChange={onChange}
          />
        )}
      </Box>
    </>
  );
};
